import { useMemo } from "react"
import { Notification } from "@nova/schemas"
import {
    NotificationContext,
    ResolvedVariable,
    resolvedVariables,
    supportsVariable,
} from "@nova/notifications"
import { selectAllFires } from "features/entity-caching/fires/selectors"
import { selectWorkspaceMembers } from "features/entity-caching/members/UserManagementSlice"
import { selectAllWorkspaces } from "features/data-management/workspace/WorkspaceManagementSlice"
import { selectAllFoldersById } from "features/data-management/workspace/context/selectors"
import { useAppSelector } from "integrations/redux/hooks"

type NotificationLookups = {
    members: ReturnType<typeof selectWorkspaceMembers>
    workspaces: ReturnType<ReturnType<typeof selectAllWorkspaces>>
    projects: ReturnType<typeof selectAllFires>
    folders: ReturnType<typeof selectAllFoldersById>
}

const resolvers: Record<
    ResolvedVariable,
    (notification: Notification, lookups: NotificationLookups) => string | undefined
> = {
    actorName: (notification, { members }) =>
        members.find((member) => member.userId === notification.createdBy)?.fullName,
    projectName: (notification, { projects }) =>
        supportsVariable.projectName(notification.data)
            ? projects[notification.data.projectId]?.name
            : undefined,
    accessResourceName: (notification, { projects, workspaces, folders }) => {
        if (!supportsVariable.accessResourceName(notification.data)) return undefined
        if (notification.data.resourceType === "workspace") {
            return workspaces.find((workspace) => workspace.id === notification.orgId)?.name
        }
        const resourceId = notification.data.resourceId
        return projects[resourceId]?.name ?? folders[resourceId]?.name
    },
    commentSnippet: (notification) => notification.commentSnippet,
}

const notificationContext = (
    notification: Notification,
    lookups: NotificationLookups
): NotificationContext =>
    Object.fromEntries(
        resolvedVariables
            .map((variable) => [variable, resolvers[variable](notification, lookups)] as const)
            .filter(([, value]) => value != null)
    )

export const useNotificationContext = (notification: Notification) => {
    const members = useAppSelector(selectWorkspaceMembers)
    const workspaces = useAppSelector(selectAllWorkspaces())
    const projects = useAppSelector(selectAllFires)
    const folders = useAppSelector(selectAllFoldersById)

    return useMemo(
        () => notificationContext(notification, { members, workspaces, projects, folders }),
        [notification, members, workspaces, projects, folders]
    )
}
